import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import ThemeToggle from "../components/ThemeToggle";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleLogin() {
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError(""); setBusy(true);
        try {
            const user = await login(email, password);
            navigate(user.needs_birth_data ? "/onboarding" : "/dashboard");
        } catch (e2) {
            setError(e2.message);
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen bg-sand-50 text-ink flex">
            <div className="hidden lg:block lg:w-1/2 relative">
                <img
                    src="https://images.unsplash.com/photo-1779498582973-fbf66b1fa483?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHw0fHxzZXJlbmUlMjB3ZWxsbmVzcyUyMHJldHJlYXQlMjBuYXR1cmV8ZW58MHx8fHwxNzgwNjE2MTM3fDA&ixlib=rb-4.1.0&q=85"
                    alt="Serene garden"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-sand-50/50 to-transparent" />
                <div className="absolute bottom-12 left-12 right-12 bg-surface/80 backdrop-blur p-8 rounded-2xl border border-ink/10">
                    <div className="sp-overline">Welcome back</div>
                    <div className="font-serif text-3xl mt-2">The sky has been waiting.</div>
                </div>
            </div>

            <div className="flex-1 flex items-center justify-center px-6 py-12">
                <div className="w-full max-w-md">
                    <div className="flex items-center justify-between">
                        <Link to="/" className="sp-overline" data-testid="login-back">← StarPath</Link>
                        <ThemeToggle />
                    </div>
                    <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif mt-6">Sign in</h1>
                    <p className="text-ink-soft mt-2">Continue your celestial travel journal.</p>

                    <button onClick={startGoogleLogin} className="sp-btn-ghost w-full mt-8" data-testid="login-google">
                        <svg viewBox="0 0 24 24" className="w-4 h-4"><path fill="#4285F4" d="M23.49 12.27c0-.86-.07-1.5-.23-2.18H12v4.13h6.61c-.13 1.08-.86 2.72-2.47 3.82l-.02.15 3.59 2.78.25.02c2.28-2.11 3.53-5.21 3.53-8.72"/><path fill="#34A853" d="M12 24c3.24 0 5.95-1.07 7.94-2.91l-3.78-2.93c-1.01.71-2.37 1.2-4.16 1.2-3.18 0-5.87-2.1-6.83-5.01l-.14.01-3.74 2.89-.05.13C3.21 21.3 7.27 24 12 24"/><path fill="#FBBC05" d="M5.17 14.35c-.25-.74-.4-1.53-.4-2.35s.14-1.6.39-2.35l-.01-.16-3.78-2.94-.12.06C.48 8.18 0 10.04 0 12s.48 3.82 1.25 5.39l3.92-3.04"/><path fill="#EB4335" d="M12 4.75c2.26 0 3.78.98 4.65 1.79l3.4-3.32C17.95 1.26 15.24 0 12 0 7.27 0 3.21 2.7 1.25 6.61l3.91 3.04C6.13 6.85 8.82 4.75 12 4.75"/></svg>
                        Continue with Google
                    </button>

                    <div className="my-6 flex items-center gap-4 text-xs text-ink-muted">
                        <div className="h-px bg-ink/10 flex-1"/>
                        <span className="sp-overline">or with email</span>
                        <div className="h-px bg-ink/10 flex-1"/>
                    </div>

                    <form onSubmit={submit} className="space-y-6">
                        <div>
                            <label className="sp-overline" htmlFor="email">Email</label>
                            <input
                                id="email"
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="sp-input"
                                placeholder="you@stars.app"
                                data-testid="login-email"
                            />
                        </div>
                        <div>
                            <label className="sp-overline" htmlFor="password">Password</label>
                            <input
                                id="password"
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="sp-input"
                                placeholder="••••••••"
                                data-testid="login-password"
                            />
                        </div>
                        {error && <div className="text-sm text-red-700" data-testid="login-error">{error}</div>}
                        <button type="submit" disabled={busy} className="sp-btn-primary w-full" data-testid="login-submit">
                            {busy ? "Aligning…" : "Sign in"}
                        </button>
                    </form>

                    <div className="mt-4 text-sm">
                        <Link to="/forgot-password" className="sp-link text-ink-muted hover:text-ink" data-testid="login-forgot-password">
                            Forgot password?
                        </Link>
                    </div>

                    <div className="mt-8 text-sm text-ink-muted">
                        New here?{" "}
                        <Link to="/register" className="sp-link text-ink" data-testid="login-to-register">Begin your chart</Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
